/**
 * WB4.5 — Map AI suggest API action ids → resolveWorkbenchActions registry rows.
 */

import type { WorkbenchActionV3 } from './action-types';
import { resolveWorkbenchActionById, type WorkbenchResolvedAction } from './workbench-kernel';
import { omitUndefined } from '@shared/utils/omitUndefined';

export interface AiSuggestedActionResolution {
  resolved: WorkbenchActionV3[];
  unknownIds: string[];
}

export interface AiSuggestUnknownActionTelemetry {
  kind: 'ai_suggest_unknown_action_id';
  unknownIds: readonly string[];
  suggestionId?: string;
  businessDomain: string;
}

type AiSuggestTelemetrySink = (event: AiSuggestUnknownActionTelemetry) => void;

let unknownActionTelemetrySink: AiSuggestTelemetrySink | undefined;

function toWorkbenchActionV3(action: WorkbenchResolvedAction): WorkbenchActionV3 {
  return omitUndefined({
    id: action.id,
    label: action.label,
    priority: action.priority,
    placement: action.placement,
    surfaces: [...action.surfaces],
    handlerRef: action.handlerRef,
    availability: action.availability,
    permissionKey: action.permissionKey,
    group: action.group,
    relationshipCapabilityId: action.relationshipCapabilityId,
    tooltip: action.tooltip,
    reason: action.reason,
    forbidden: action.forbidden,
    commandOnly: action.commandOnly,
    riskTier: action.riskTier,
    safety: action.safety,
  });
}

/** Test hook — capture unknown-id telemetry without console noise. */
export function setAiSuggestUnknownActionTelemetrySink(
  sink: AiSuggestTelemetrySink | undefined
): void {
  unknownActionTelemetrySink = sink;
}

export function emitAiSuggestUnknownActionTelemetry(event: AiSuggestUnknownActionTelemetry): void {
  unknownActionTelemetrySink?.(event);
  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn('[workbench:ai-suggest]', event);
  }
}

/**
 * Resolves suggest API ids through the same registry SSOT as palette/inspector.
 * Unknown ids are returned for preview-only fallback (no kernel dispatch).
 */
export function resolveAiSuggestedActionsFromIds(
  suggestedActionIds: readonly string[] | undefined,
  registryActions: readonly WorkbenchResolvedAction[]
): AiSuggestedActionResolution {
  if (!suggestedActionIds?.length) {
    return { resolved: [], unknownIds: [] };
  }

  const resolved: WorkbenchActionV3[] = [];
  const unknownIds: string[] = [];
  const seen = new Set<string>();

  for (const actionId of suggestedActionIds) {
    const trimmed = actionId.trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);

    const registryAction = resolveWorkbenchActionById(trimmed, registryActions);
    if (!registryAction) {
      unknownIds.push(trimmed);
      continue;
    }

    resolved.push(toWorkbenchActionV3(registryAction));
  }

  return { resolved, unknownIds };
}
